/**
 * Grand Quack Auto — car entity factory.
 *
 * Creates a primitive-based car mesh + cannon-es RaycastVehicle.
 * Returns a VehicleRef that's registered with the physics system for
 * automatic body→mesh sync.
 *
 * See research.md §3 "Vehicle physics with cannon-es RaycastVehicle".
 */

import * as CANNON from 'cannon-es';
import * as THREE from 'three';
import { PALETTE, VEHICLE } from '../config';
import { addPhysicsBody } from '../systems/physicsSystem';
import type { VehicleRef, World } from '../types';

interface CarOpts {
  position?: readonly [number, number, number];
  color?: number;
  isPolice?: boolean;
}

const CAR_COLORS = [0xcc4444, 0x4488cc, 0x44cc44, 0xcccc44, 0xcc8844];

export function createCar(world: World, opts: CarOpts = {}): VehicleRef {
  const [hx, hy, hz] = VEHICLE.chassisHalfExtents;
  const color = opts.isPolice ? 0xffffff : (opts.color ?? CAR_COLORS[world.vehicles.length % CAR_COLORS.length]!);
  const mat = new THREE.MeshToonMaterial({ color });

  const group = new THREE.Group();
  group.name = opts.isPolice ? 'policeCar' : 'car';

  // Chassis body.
  const chassis = new THREE.Mesh(new THREE.BoxGeometry(hx * 2, hy * 2, hz * 2), mat);
  chassis.castShadow = true;
  group.add(chassis);

  // Cabin on top.
  const cabin = new THREE.Mesh(
    new THREE.BoxGeometry(hx * 1.6, hy * 1.2, hz * 0.9),
    new THREE.MeshToonMaterial({ color: opts.isPolice ? 0xdddddd : color }),
  );
  cabin.position.set(0, hy * 1.1, -hz * 0.15);
  cabin.castShadow = true;
  group.add(cabin);

  // Police light bar.
  if (opts.isPolice) {
    const barR = new THREE.Mesh(
      new THREE.BoxGeometry(0.3, 0.15, 0.2),
      new THREE.MeshBasicMaterial({ color: PALETTE.policeRed }),
    );
    barR.position.set(-0.3, hy * 1.8, -hz * 0.15);
    const barB = new THREE.Mesh(
      new THREE.BoxGeometry(0.3, 0.15, 0.2),
      new THREE.MeshBasicMaterial({ color: PALETTE.policeBlue }),
    );
    barB.position.set(0.3, hy * 1.8, -hz * 0.15);
    group.add(barR, barB);
  }

  // Wheel meshes.
  // CylinderGeometry's axis is Y by default, but cannon-es RaycastVehicle's
  // axleLocal is X — so bake a 90° rotation into the geometry once, and let
  // vehicleSystem.render apply the cannon worldTransform directly per-frame.
  // Without this, the wheel's quaternion overwrite leaves cylinders standing
  // upright (edge-on to the camera = invisible).
  const wheelGeom = new THREE.CylinderGeometry(
    VEHICLE.wheelRadius, VEHICLE.wheelRadius, VEHICLE.wheelWidth, 8,
  );
  wheelGeom.rotateZ(Math.PI / 2);
  const wheelMat = new THREE.MeshToonMaterial({ color: 0x333333 });
  const wheelMeshes: THREE.Mesh[] = [];
  for (const wp of VEHICLE.wheelPositions) {
    const wm = new THREE.Mesh(wheelGeom, wheelMat);
    wm.position.set(wp[0], wp[1], wp[2]);
    wm.castShadow = true;
    group.add(wm);
    wheelMeshes.push(wm);
  }

  // Position the group.
  const pos = opts.position ?? [0, 1, 0];
  group.position.set(pos[0], pos[1], pos[2]);
  world.scene.add(group);

  // ── cannon-es chassis body ──────────────────────────────────────────
  const chassisBody = new CANNON.Body({
    mass: VEHICLE.chassisMass,
    shape: new CANNON.Box(new CANNON.Vec3(hx, hy, hz)),
  });
  chassisBody.position.set(pos[0], pos[1], pos[2]);
  chassisBody.allowSleep = false;
  chassisBody.angularDamping = 0.3;  // resist rolling/flipping
  chassisBody.linearDamping = 0.05;

  // ── RaycastVehicle ──────────────────────────────────────────────────
  const vehicle = new CANNON.RaycastVehicle({
    chassisBody,
    indexRightAxis: 0,
    indexForwardAxis: 2,
    indexUpAxis: 1,
  });

  for (const wp of VEHICLE.wheelPositions) {
    vehicle.addWheel({
      chassisConnectionPointLocal: new CANNON.Vec3(wp[0], wp[1], wp[2]),
      directionLocal: new CANNON.Vec3(0, -1, 0),
      axleLocal: new CANNON.Vec3(-1, 0, 0),
      suspensionRestLength: VEHICLE.suspensionRestLength,
      suspensionStiffness: VEHICLE.wheelSuspensionStiffness,
      dampingCompression: VEHICLE.dampingCompression,
      dampingRelaxation: VEHICLE.dampingRelaxation,
      frictionSlip: VEHICLE.wheelFrictionSlip,
      rollInfluence: VEHICLE.wheelRollInfluence,
      radius: VEHICLE.wheelRadius,
      maxSuspensionTravel: VEHICLE.maxSuspensionTravel,
      maxSuspensionForce: VEHICLE.maxSuspensionForce,
    });
  }

  if (world.physics) {
    vehicle.addToWorld(world.physics.world);
  }

  // Register chassis body→group pair for physics sync.
  addPhysicsBody(world, chassisBody, group);

  const ref: VehicleRef = {
    chassisBody, vehicle, group, wheelMeshes,
    originalSpawn: [pos[0], pos[1], pos[2]] as const,
  };
  world.vehicles.push(ref);
  return ref;
}
